// src/components/vault/ui/VaultTopNav.tsx
import { useRef, useEffect, useState, useCallback } from "react";
import { useBreakpoint } from '../../../hooks/useIsMobile';
import { motion } from "motion/react";
import {
  User,
  UserCheck,
  FileText,
  Home,
  Building2,
  Users,
  Check,
  ChevronLeft,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { Subfolder, Status } from "../../../redux/interfaces/folder.types";
import { getOwnerNameFromForms } from "../../../utils/subfolders/ownerName";
import { getSameLabelOrdinal } from "../../../utils/subfolders/roleOrdinal";
import { isProofOfAddressProcessing } from "../utils/requirementGrouping";

// Map subfolder slugs to icons
const subfolderIcons: Record<string, LucideIcon> = {
  "tenant-pf": User,
  "tenant-pm": Building2,
  "tenant-pf-cja": User,
  "tenant-pm-cja": Building2,
  "guarantor-pf": UserCheck,
  "guarantor-pf-cja": UserCheck,
  "guarantor-pm": Building2,
  "guarantor-pm-cja": Building2,
  "guarantor-investigation-pf": UserCheck,
  "guarantor-investigation-pm": Building2,
  collateral: Home,
  "lease-contract": FileText,
  "lease-contract-pf": FileText,
  "lease-contract-pm": FileText,
  // MB-431 secondary landlords: same PF/PM icon convention as the other roles.
  "landlord-pf": User,
  "landlord-pm": Building2,
  "owner-representative": Users,
  "owner-representative-pm": Users,
};

// Human readable names for subfolders
const subfolderNames: Record<string, string> = {
  "tenant-pf": "Documentos del Inquilino",
  "tenant-pm": "Documentos del Inquilino",
  "tenant-pf-cja": "Documentos del Inquilino",
  "tenant-pm-cja": "Documentos del Inquilino",
  "guarantor-pf": "Documentos del Fiador",
  "guarantor-pf-cja": "Documentos del Fiador",
  "guarantor-pm": "Documentos del Fiador",
  "guarantor-pm-cja": "Documentos del Fiador",
  "guarantor-investigation-pf": "Documentos del Fiador",
  "guarantor-investigation-pm": "Documentos del Fiador",
  collateral: "Inmueble en Garantia",
  "lease-contract": "Contrato de Arrendamiento",
  "lease-contract-pf": "Contrato de Arrendamiento",
  "lease-contract-pm": "Contrato de Arrendamiento",
  "landlord-pf": "Documentos del Propietario",
  "landlord-pm": "Documentos del Propietario",
  "owner-representative": "Rep. del Propietario",
  "owner-representative-pm": "Rep. del Propietario",
};

// Display name per subfolder. When several subfolders share a role (one per
// participant), append the participant name from metadata so they are distinguishable.
function getDisplayName(
  subfolder: Subfolder,
  allSubfolders?: Subfolder[],
): string {
  const base = subfolderNames[subfolder.slug] ?? subfolder.name;
  const fullName = subfolder.metadata?.full_name;
  // Owner representatives: show which owner they represent (the key relationship).
  // Resolve the owner name live from its forms so it stays fresh; fall back to the
  // denormalized represents_name captured when the representative was created.
  if (subfolder.slug.startsWith("owner-representative")) {
    const ownerId = subfolder.metadata?.represents_subfolder_id;
    const owner = allSubfolders?.find((sf) => sf.id === ownerId);
    const ownerName =
      (owner && (getOwnerNameFromForms(owner) || owner.metadata?.full_name?.trim())) ||
      subfolder.metadata?.represents_name;
    if (ownerName) return `${base} · ${ownerName}`;
    return base;
  }
  if (fullName) return `${base} · ${fullName}`;
  // Fallback so same-role tabs stay distinguishable when the name is missing.
  // Number by role from the subfolders list instead of `person_index`: DocVault
  // numbers per-template, so the principal and the first additional of the same
  // role can both be person_index === 1 and would otherwise render identically.
  const ordinal = allSubfolders
    ? getSameLabelOrdinal(
        subfolder,
        allSubfolders,
        (sf) => subfolderNames[sf.slug] ?? sf.name,
      )
    : null;
  if (ordinal) return `${base} ${ordinal}`;
  return base;
}

interface VaultTopNavProps {
  subfolders: Subfolder[];
  activeTab: string;
  onTabChange: (tabSlug: string) => void;
}

// ---------------------------------------------------------------------------
// Mobile & Tablet: scrollable pill tabs with gradient fades, peek animation, ARIA
// On mobile phones (<768px): larger touch targets (44px) and font (14px)
// On tablets (768-1023px): original sizing preserved
// ---------------------------------------------------------------------------
// Tracks horizontal scroll position of `scrollRef` and exposes whether there is more
// content to the left/right (for fades + arrows) plus an arrow-scroll handler. Shared by
// the mobile pill tabs and the desktop tab bar so both show the same scroll affordances.
function useScrollIndicators(
  scrollRef: React.RefObject<HTMLDivElement | null>,
  deps: React.DependencyList,
) {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, [scrollRef]);

  const handleScrollArrow = useCallback(
    (direction: "left" | "right") => {
      const el = scrollRef.current;
      if (!el) return;
      const scrollAmount = el.clientWidth * 0.6;
      el.scrollBy({
        left: direction === "right" ? scrollAmount : -scrollAmount,
        behavior: "smooth",
      });
    },
    [scrollRef],
  );

  // Re-check on mount, when deps (e.g. subfolders) change, and after layout settles.
  useEffect(() => {
    updateScrollState();
    const t1 = setTimeout(updateScrollState, 150);
    const t2 = setTimeout(updateScrollState, 500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, updateScrollState]);

  // Update on scroll and on window resize (overflow can appear/disappear).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState, scrollRef]);

  // Peek animation on first visit — nudge right then snap back to hint there is more.
  useEffect(() => {
    const storageKey = "vault-tabs-peek-shown";
    if (sessionStorage.getItem(storageKey)) return;
    let snapBackTimer: ReturnType<typeof setTimeout>;
    // Wait for layout to settle before checking overflow.
    const checkTimer = setTimeout(() => {
      const el = scrollRef.current;
      if (!el) return;
      if (el.scrollWidth <= el.clientWidth) return;
      sessionStorage.setItem(storageKey, "true");
      el.scrollTo({ left: 80, behavior: "smooth" });
      snapBackTimer = setTimeout(() => {
        el.scrollTo({ left: 0, behavior: "smooth" });
      }, 600);
    }, 1000);
    return () => { clearTimeout(checkTimer); clearTimeout(snapBackTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { canScrollLeft, canScrollRight, handleScrollArrow, updateScrollState };
}

function MobilePillTabs({ subfolders, activeTab, onTabChange }: VaultTopNavProps) {
  const { isMobile } = useBreakpoint();
  const scrollRef = useRef<HTMLDivElement>(null);
  const pillRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const { canScrollLeft, canScrollRight, handleScrollArrow } = useScrollIndicators(
    scrollRef,
    [subfolders],
  );

  // Auto-scroll active pill into view
  useEffect(() => {
    const activePill = pillRefs.current.get(activeTab);
    if (activePill && scrollRef.current) {
      activePill.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [activeTab]);

  return (
    <div className="relative py-2">
      {/* Left gradient fade */}
      <div
        className={cn(
          "absolute left-0 top-0 bottom-0 w-16 z-10 pointer-events-none transition-opacity duration-200",
          canScrollLeft ? "opacity-100" : "opacity-0"
        )}
        style={{ background: "linear-gradient(to right, white 25%, transparent 100%)" }}
      />
      <button
        type="button"
        aria-label="Desplazar tabs a la izquierda"
        onClick={() => handleScrollArrow("left")}
        className={cn(
          "absolute left-1 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center",
          "w-7 h-7 rounded-full bg-white shadow-md border border-slate-200",
          "active:scale-90 transition-all duration-200",
          canScrollLeft ? "opacity-100 scale-100" : "opacity-0 scale-75 pointer-events-none"
        )}
        tabIndex={canScrollLeft ? 0 : -1}
      >
        <ChevronLeft className="w-4 h-4 text-slate-500" />
      </button>

      <div
        ref={scrollRef}
        role="tablist"
        aria-label="Secciones de documentos"
        aria-orientation="horizontal"
        className="flex gap-2 overflow-x-auto px-3 pb-0"
        style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' } as React.CSSProperties}
      >
        {subfolders.map((subfolder) => {
          const Icon = subfolderIcons[subfolder.slug] || FileText;
          const displayName = getDisplayName(subfolder, subfolders);
          const isActive = activeTab === subfolder.id;
          const hasProcessingProofOfAddress = Boolean(
            subfolder.requirements?.some(isProofOfAddressProcessing),
          );
          const statusIndicator = getStatusIndicator(
            subfolder.status,
            hasProcessingProofOfAddress,
          );
          const statusLabel = getStatusLabel(
            subfolder.status,
            hasProcessingProofOfAddress,
          );

          return (
            <motion.button
              key={subfolder.id}
              ref={(el) => { if (el) pillRefs.current.set(subfolder.id, el); }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 400, damping: 17 }}
              onClick={() => onTabChange(subfolder.id)}
              role="tab"
              aria-selected={isActive}
              aria-label={`${displayName}${statusLabel ? ` — ${statusLabel}` : ""}`}
              tabIndex={isActive ? 0 : -1}
              className={cn(
                "relative flex items-center gap-1.5 rounded-full flex-shrink-0",
                // Mobile phones: larger touch targets (44px) and font (14px)
                // Tablets: original sizing (36px height, 12px font)
                isMobile ? "px-4 h-11 text-sm" : "px-3 h-9 text-xs",
                "font-medium transition-all duration-200 whitespace-nowrap",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#28397a] focus-visible:ring-offset-1",
                isActive
                  ? "bg-[#28397a] text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              )}
            >
              <Icon className={cn("flex-shrink-0", isMobile ? "w-4 h-4" : "w-3.5 h-3.5")} />
              <span>{displayName}</span>
              {/* Status dot */}
              {subfolder.status === Status.Valid &&
              !hasProcessingProofOfAddress ? (
                <span
                  className={cn(
                    "absolute -top-0.5 -right-0.5 flex items-center justify-center rounded-full ring-1",
                    isMobile ? "w-3.5 h-3.5" : "w-3 h-3",
                    isActive ? "bg-emerald-400 ring-[#28397a]" : "bg-emerald-500 ring-white"
                  )}
                  aria-hidden="true"
                >
                  <Check className="w-2 h-2 text-white" strokeWidth={3} />
                </span>
              ) : statusIndicator && (
                <span
                  className={cn(
                    "absolute -top-0.5 -right-0.5 rounded-full ring-1",
                    isMobile ? "w-2.5 h-2.5" : "w-2 h-2",
                    isActive ? "ring-[#28397a]" : "ring-white",
                    statusIndicator
                  )}
                  aria-hidden="true"
                />
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Right gradient fade */}
      <div
        className={cn(
          "absolute right-0 top-0 bottom-0 w-16 z-10 pointer-events-none transition-opacity duration-200",
          canScrollRight ? "opacity-100" : "opacity-0"
        )}
        style={{ background: "linear-gradient(to left, white 25%, transparent 100%)" }}
      />
      <button
        type="button"
        aria-label="Desplazar tabs a la derecha"
        onClick={() => handleScrollArrow("right")}
        className={cn(
          "absolute right-1 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center",
          "w-7 h-7 rounded-full bg-white shadow-md border border-slate-200",
          "active:scale-90 transition-all duration-200",
          canScrollRight ? "opacity-100 scale-100" : "opacity-0 scale-75 pointer-events-none"
        )}
        tabIndex={canScrollRight ? 0 : -1}
      >
        <ChevronRight className="w-4 h-4 text-slate-500" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main export: routes to pill tabs (mobile/tablet) or desktop tabs
// ---------------------------------------------------------------------------
export function VaultTopNav({
  subfolders,
  activeTab,
  onTabChange,
}: VaultTopNavProps) {
  const navRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const { isDesktop } = useBreakpoint();
  const { canScrollLeft, canScrollRight, handleScrollArrow, updateScrollState } =
    useScrollIndicators(scrollRef, [subfolders]);

  // Update indicator position when active tab changes (desktop only)
  // NOTE: Hook must be called before any early return (Rules of Hooks)
  useEffect(() => {
    if (!isDesktop) return;
    const activeButton = tabRefs.current.get(activeTab);
    const nav = navRef.current;
    if (activeButton && nav) {
      const navRect = nav.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      setIndicatorStyle({
        left: buttonRect.left - navRect.left,
        width: buttonRect.width,
      });
    }
  }, [activeTab, subfolders, isDesktop]);

  // Keep the active tab visible and refresh the scroll affordances (desktop only).
  useEffect(() => {
    if (!isDesktop) return;
    tabRefs.current
      .get(activeTab)
      ?.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });
    updateScrollState();
  }, [activeTab, subfolders, isDesktop, updateScrollState]);

  // Mobile & Tablet: pill tabs with UX improvements
  if (!isDesktop) {
    return <MobilePillTabs subfolders={subfolders} activeTab={activeTab} onTabChange={onTabChange} />;
  }

  // Desktop: full tab bar with animated indicator + horizontal scroll affordances
  return (
    <div className="flex justify-center py-4 px-4">
      <div className="relative max-w-full">
        {/* Left gradient fade */}
        <div
          className={cn(
            "absolute left-0 top-0 bottom-0 w-16 z-10 pointer-events-none rounded-l-xl transition-opacity duration-200",
            canScrollLeft ? "opacity-100" : "opacity-0"
          )}
          style={{ background: "linear-gradient(to right, white 30%, transparent 100%)" }}
        />
        <button
          type="button"
          aria-label="Desplazar secciones a la izquierda"
          onClick={() => handleScrollArrow("left")}
          className={cn(
            "absolute left-1 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center",
            "w-7 h-7 rounded-full bg-[#28397a] shadow-md border border-[#28397a]",
            "hover:bg-[#28397a]/90 active:scale-90 transition-all duration-200",
            canScrollLeft ? "opacity-100 scale-100" : "opacity-0 scale-75 pointer-events-none"
          )}
          tabIndex={canScrollLeft ? 0 : -1}
        >
          <ChevronLeft className="w-4 h-4 text-white" />
        </button>

        <div
          ref={scrollRef}
          className="overflow-x-auto [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" } as React.CSSProperties}
        >
          <nav
            ref={navRef}
            className="relative inline-flex items-center p-1 rounded-xl border border-slate-200/60 bg-white/80 backdrop-blur-sm shadow-sm"
          >
          {/* Animated sliding background */}
          <motion.div
            className="absolute h-[calc(100%-8px)] bg-[#28397a]/5 rounded-lg"
            initial={false}
            animate={{
              left: indicatorStyle.left,
              width: indicatorStyle.width,
            }}
            transition={{
              type: "tween",
              duration: 0.25,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            style={{ top: 4 }}
          />

          {subfolders.map((subfolder, index) => {
            const Icon = subfolderIcons[subfolder.slug] || FileText;
            const displayName =
              getDisplayName(subfolder, subfolders);
            const isActive = activeTab === subfolder.id;
            const hasProcessingProofOfAddress = Boolean(
              subfolder.requirements?.some(isProofOfAddressProcessing),
            );
            const statusIndicator = getStatusIndicator(
              subfolder.status,
              hasProcessingProofOfAddress,
            );
            const isLast = index === subfolders.length - 1;

            return (
              <div key={subfolder.id} className="flex items-center">
                <button
                  ref={(el) => {
                    if (el) tabRefs.current.set(subfolder.id, el);
                  }}
                  onClick={() => onTabChange(subfolder.id)}
                  className={cn(
                    "relative flex items-center gap-2 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2",
                    isActive
                      ? "text-[#28397a]"
                      : "text-slate-500 hover:text-slate-700"
                  )}
                >
                  {/* Icon with circular background */}
                  <span
                    className={cn(
                      "flex items-center justify-center w-6 h-6 rounded-full transition-all duration-200",
                      isActive
                        ? "bg-[#28397a] text-white"
                        : "bg-slate-100 text-slate-500"
                    )}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <span
                    title={displayName}
                    className="hidden sm:inline relative max-w-[220px] overflow-hidden align-middle"
                  >
                    {/* Invisible bold text reserves the (bold) width so activating a tab
                        doesn't shift layout; truncate keeps everything on one line. */}
                    <span className="block font-bold invisible whitespace-nowrap truncate">
                      {displayName}
                    </span>
                    {/* Visible text */}
                    <span
                      className={cn(
                        "absolute inset-0 whitespace-nowrap truncate transition-all duration-200",
                        isActive ? "font-bold" : "font-medium"
                      )}
                    >
                      {displayName}
                    </span>
                  </span>

                  {/* Status indicator */}
                  {subfolder.status === Status.Valid &&
                  !hasProcessingProofOfAddress ? (
                    <span className="absolute -top-1 -right-1 flex items-center justify-center w-3.5 h-3.5 rounded-full bg-emerald-500 ring-2 ring-white">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </span>
                  ) : statusIndicator && (
                    <span
                      className={cn(
                        "absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-white",
                        statusIndicator
                      )}
                    />
                  )}
                </button>

                {/* Separator line */}
                {!isLast && (
                  <div className="w-px h-5 bg-slate-200/80 mx-2" />
                )}
              </div>
            );
          })}
          </nav>
        </div>

        {/* Right gradient fade */}
        <div
          className={cn(
            "absolute right-0 top-0 bottom-0 w-16 z-10 pointer-events-none rounded-r-xl transition-opacity duration-200",
            canScrollRight ? "opacity-100" : "opacity-0"
          )}
          style={{ background: "linear-gradient(to left, white 30%, transparent 100%)" }}
        />
        <button
          type="button"
          aria-label="Desplazar secciones a la derecha"
          onClick={() => handleScrollArrow("right")}
          className={cn(
            "absolute right-1 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center",
            "w-7 h-7 rounded-full bg-[#28397a] shadow-md border border-[#28397a]",
            "hover:bg-[#28397a]/90 active:scale-90 transition-all duration-200",
            canScrollRight ? "opacity-100 scale-100" : "opacity-0 scale-75 pointer-events-none"
          )}
          tabIndex={canScrollRight ? 0 : -1}
        >
          <ChevronRight className="w-4 h-4 text-white" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function getStatusIndicator(
  status: Status,
  hasProcessingProofOfAddress = false,
): string | null {
  if (hasProcessingProofOfAddress) return "bg-blue-500 animate-pulse";

  switch (status) {
    case Status.Valid:
      return "bg-emerald-500";
    case Status.PendingReview:
      return "bg-emerald-500";
    case Status.Invalid:
      return "bg-red-500";
    case Status.PendingUpload:
    default:
      return null;
  }
}

function getStatusLabel(
  status: Status,
  hasProcessingProofOfAddress = false,
): string | null {
  if (hasProcessingProofOfAddress) return "Analizando comprobante";

  switch (status) {
    case Status.Valid:
      return "Completado";
    case Status.PendingReview:
      return "Completado";
    case Status.Invalid:
      return "Requiere corrección";
    case Status.PendingUpload:
      return "Pendiente";
    default:
      return null;
  }
}

// Alternative minimal version with just icons
interface VaultTopNavMinimalProps {
  subfolders: Subfolder[];
  activeTab: string;
  onTabChange: (tabSlug: string) => void;
}

export function VaultTopNavMinimal({
  subfolders,
  activeTab,
  onTabChange,
}: VaultTopNavMinimalProps) {
  return (
    <div className="flex justify-center py-4">
      <div className="inline-flex items-center p-1.5 rounded-2xl border border-slate-200/60 bg-white/80 backdrop-blur-sm shadow-sm">
        {subfolders.map((subfolder, index) => {
          const Icon = subfolderIcons[subfolder.slug] || FileText;
          const displayName = getDisplayName(subfolder, subfolders);
          const isActive = activeTab === subfolder.id;
          const hasProcessingProofOfAddress = Boolean(
            subfolder.requirements?.some(isProofOfAddressProcessing),
          );
          const statusIndicator = getStatusIndicator(
            subfolder.status,
            hasProcessingProofOfAddress,
          );
          const isLast = index === subfolders.length - 1;

          return (
            <div key={subfolder.id} className="flex items-center">
              <button
                onClick={() => onTabChange(subfolder.id)}
                title={displayName}
                className={cn(
                  "relative flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-200",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                )}
              >
                {/* Icon with circular background */}
                <span
                  className={cn(
                    "flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200",
                    isActive
                      ? "bg-[#28397a] text-white"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200/80"
                  )}
                >
                  <Icon className="w-4 h-4" />
                </span>
                {/* Status dot indicator */}
                {statusIndicator && (
                  <span
                    className={cn(
                      "absolute top-0 right-0 w-2 h-2 rounded-full ring-2 ring-white",
                      statusIndicator
                    )}
                  />
                )}
              </button>

              {/* Separator line */}
              {!isLast && (
                <div className="w-px h-6 bg-slate-200/80 mx-2" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
